import { BaseApiRequestPayload, GetCouponRatesRequestPayload, GetReservationRequestPayload, GetUnitAvailabilityRequestPayload, GetUnitListRequestPayload, PostMarketingSurveyRequestPayload, PostReservationFeeRequestPayload, PostReservationSaveDocumentsPayload, PostScaBillingInfoUpdate, PostScaMoveInPrepare, PostScaReservationWithFeePrepare } from '@r6digital/rapidstor-types/react/api/Requests';
import { Charge, chargeTransformer } from '@r6digital/rapidstor-types/react/entities/Charge';
import { corpTransformer } from '@r6digital/rapidstor-types/react/entities/Corp';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location, {
    locationTransformer,
} from '@r6digital/rapidstor-types/react/entities/Location';
import {
    groupByLocationCode,
    MarketingSource,
} from '@r6digital/rapidstor-types/react/entities/MarketingSource';
import {
    TrackEvent,
    TrackEventRequest,
} from '@r6digital/rapidstor-types/react/entities/Tracking';
import { unitTypeTransformer } from '@r6digital/rapidstor-types/react/entities/UnitType';
import { UnitList } from '@r6digital/rapidstor-types/react/rtk-query/UnitList';
import {
    RapidStorErrorResponse,
    RapidStorPersona,
    RapidStorResponse,
    RapidStorUnitAvailability,
    RapidStorUnitList,
    ScaPrepareResponse,
} from '@r6digital/rapidstor-types/types/Api';
import RapidStorCharge from '@r6digital/rapidstor-types/types/Charge';
import Corporation from '@r6digital/rapidstor-types/types/Corporation';
import { RapidStorCouponRatesResponse } from '@r6digital/rapidstor-types/types/Coupon';
import { PostProcessedDescriptor } from '@r6digital/rapidstor-types/types/Descriptor';
import {
    RapidStorBoxShopCouponResponse,
    RapidStorMerchandise,
} from '@r6digital/rapidstor-types/types/Merchandise';
import {
    BaseReservation,
    CostRetrieveFormatted,
    CostRetrieveResponse,
    RapidStorCostRetrieveRequest,
    RapidStorMoveInInit,
    RapidStorMoveInResponse,
    ReservationPatchRequest,
} from '@r6digital/rapidstor-types/types/Reservation';
import { RapidStorReservationRequest } from '@r6digital/rapidstor-types/types/Reservation';
import Site from '@r6digital/rapidstor-types/types/Site';

import { PersonaType } from '@/screens';
import { HttpMethod } from '@/types/Http';
import { initApplication } from '@/utils/application';
import {
    createApi,
    fetchBaseQuery,
    retry,
} from '@reduxjs/toolkit/query/react';

initApplication();

const baseQuery = fetchBaseQuery({
    baseUrl: window.rapidstor_settings.rapidstor_options.baseURL,

    prepareHeaders: (headers) => {
        if (window.rapidstor_settings.rapidstor_options.locationCode && window.rapidstor_settings.rapidstor_options.corpCode) {
            headers.set('site', window.rapidstor_settings.rapidstor_options.locationCode);
            headers.set('corp', window.rapidstor_settings.rapidstor_options.corpCode);
        }
        return headers;
    }
});

const descriptorMinArea = (descriptor: PostProcessedDescriptor): number => {
    const sizeCriteria = descriptor.criteria?.include?.sizes ?? [];
    return Math.min(
        ...descriptor.unitTypes.map((u) => u.dcArea),
        ...sizeCriteria.map((s) => s.width * s.length)
    );
};

export const descriptorTransformer = (
    descriptor: PostProcessedDescriptor,
    location?: Location
): Descriptor => {
    return {
        _id: descriptor._id as string,
        locationCode: descriptor.sLocationCode,
        corpCode: descriptor.sCorpCode,
        name: descriptor.name,
        description: descriptor.description,
        descriptions: descriptor.descriptions,
        tags: descriptor.tags,
        picture: descriptor.picture,
        slides: descriptor.slides,
        defaultInsuranceCoverage: descriptor.defaultInsuranceCoverage,
        deals: descriptor.deals,
        enabled: descriptor.enabled,
        hidden: descriptor.hidden,
        unitTypes: descriptor.unitTypes
            .map((u) => unitTypeTransformer(u, location))
            .filter((item) =>
                location?.rapidstor.hideUnitsMinAvailable
                    ? item.units.length >= location.rapidstor.minAvailableUnits
                    : true
            ),
        ordinalPosition: descriptor.ordinalPosition,
        area: descriptorMinArea(descriptor),
        specialText: descriptor.specialText,
        jblOptionText: descriptor.jblOptionText,
        upgradesTo: descriptor.upgradesTo,
        useForCarousel: descriptor.useForCarousel,
    };
};

const coalesceDescriptors = (descriptors: Descriptor[]): Descriptor[] => {
    // If a descriptor is useForCarousel, and has no availabile units, and has an upgrade path,
    // then we want to show the upgrade path instead.
    const out: Descriptor[] = [];
    const descriptorMap = Object.fromEntries(
        descriptors.map((d) => [d._id, d])
    );
    for (const descriptor of descriptors) {
        if (
            descriptor.useForCarousel &&
            !descriptor.unitTypes.find(ut => ut.hasComputedAvailable) &&
            descriptor.upgradesTo?.length
        ) {
            const firstAvailableUpgrade = descriptor.upgradesTo
                .map(({ _id },) => descriptorMap[_id as string])
                .filter(d => d)
                .find(d => d.unitTypes.find(ut => ut.hasComputedAvailable));
            if (!firstAvailableUpgrade) {
                out.push(descriptor);
                continue;
            }
            out.push({
                ...firstAvailableUpgrade,
                useForCarousel: true,
                upgradesTo: descriptor.upgradesTo.filter(({ _id }) => _id !== firstAvailableUpgrade._id),
                name: descriptor.name,
                ordinalPosition: descriptor.ordinalPosition,
            });
        } else {
            out.push(descriptor);
        }
    }
    // Remove duplicate ids and return.
    return out.filter((d, i) => i === out.findIndex(d0 => d0._id === d._id));
};

export const rapidstorApi = createApi({
    reducerPath: 'rapidstorApi',
    baseQuery: retry(baseQuery, { maxRetries: 3 }),
    keepUnusedDataFor: 3600,
    tagTypes: ['Location', 'UnitList', 'Personas'],
    endpoints: (builder) => ({
        getCorp: builder.query({
            query: ({ corpCode }: { corpCode: string }) => ({
                url: `/api/v1/corp/${corpCode}`,
            }),
            transformResponse: (resp: RapidStorResponse<Corporation.Base>) => {
                return corpTransformer(resp.results);
            },
        }),
        getLocationByCode: builder.query({
            query: ({ locationCode }) => `/api/v1/locations/${locationCode}`,
            transformResponse: (resp: RapidStorResponse<Site>) => locationTransformer(resp.results),
            providesTags: ['Location'],
        }),
        getUnitListByType: builder.query<
            UnitList,
            GetUnitListRequestPayload
        >({
            query: ({ locationCodes, persona, unitTypeNames, unitTypeRegex }) => ({
                url: `/api/v1/unit-list/by-type`,
                method: HttpMethod.POST,
                body: {
                    sites: locationCodes,
                    persona,
                    unitTypeNames,
                    unitTypeRegex,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorUnitList>): UnitList => {
                if (
                    resp.results.descriptors.length === 0 ||
                    resp.results.unitTypes.length === 0
                ) {
                    return {
                        descriptors: [],
                        unitTypes: [],
                    };
                }

                return {
                    descriptors: coalesceDescriptors(resp.results.descriptors
                        .map((d): Descriptor => {
                            return descriptorTransformer(d, undefined);
                        })
                        .filter((d) => d.unitTypes.length > 0 && !d.hidden && d.enabled)),
                    unitTypes: resp.results.unitTypes.map((u) => {
                        return unitTypeTransformer(u, undefined);
                    }),
                };
            },
        }),
        getPersonas: builder.query<PersonaType[], BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: `/api/v1/${locationCode}/personas`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorPersona[]>): PersonaType[] => {
                return resp.results.map((persona) => {
                    return {
                        _id: persona._id,
                        label: persona.name,
                        filter: '',
                        icon: persona.image || '',
                        text: persona.description || '',
                        available: persona.useForJourneySelection || false,
                        customerType: persona.assignCustomerType || null
                    };
                });
            },
            providesTags: ['Personas'],
        }),
        getReservation: builder.query<BaseReservation, GetReservationRequestPayload>({
            query: ({ locationCode, token, corpCode }: GetReservationRequestPayload) => ({
                url: `/api/v1/reservation/${token}`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>): BaseReservation => {
                return resp.results;
            },
        }),
        postReservation: builder.mutation<BaseReservation, RapidStorReservationRequest>({
            query: ({ locationCode, corpCode, payload }: RapidStorReservationRequest) => ({
                url: `/api/v1/reservation`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
                return resp.results;
            },
        }),
        patchReservation: builder.mutation<BaseReservation, ReservationPatchRequest>({
            query: ({ locationCode, corpCode, payload, token }) => ({
                url: `/api/v1/reservations/${token}`,
                method: HttpMethod.PATCH,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<{ reservation: BaseReservation }>
            ) => {
                return resp.results.reservation;
            },
        }),
        postReservationFee: builder.mutation<BaseReservation, PostReservationFeeRequestPayload>({
            query: ({ locationCode, corpCode, payload, token }: PostReservationFeeRequestPayload) => ({
                url: `/api/v1/reservation/${token}/add-fee`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
                return resp.results;
            },
        }),
        postSaveQuote: builder.mutation<BaseReservation, RapidStorReservationRequest>({
            query: ({ locationCode, corpCode, payload }) => ({
                url: `/api/v1/reservation/save-quote`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
                return resp.results;
            },
        }),
        postTrackEvent: builder.mutation<TrackEvent, TrackEventRequest>({
            query: ({ corpToken, locationToken, payload }) => ({
                url: `/rapidstor/${corpToken}/${locationToken}/api/track`,
                method: HttpMethod.POST,
                body: payload,
            }),
        }),
        getMoveInCostRetrieve: builder.mutation({
            query: ({ location, corpCode, reservationId }) => ({
                url: `/api/v1/move-in/${reservationId}/cost-retrieve`,
                method: HttpMethod.GET,
                headers: {
                    site: location,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<CostRetrieveResponse>) => {
                return resp.results;
            },
            transformErrorResponse: (resp: { status: number, data: RapidStorErrorResponse<string>}) => {
                return resp.data.error;
            }
        }),
        getCharges: builder.query<Charge[], BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: `/api/v1/locations/${locationCode}/charges`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorCharge[]>) => {
                return resp.results.map((charge) => {
                    return chargeTransformer(charge);
                });
            },
        }),
        getUnitAvailability: builder.mutation<RapidStorUnitAvailability, GetUnitAvailabilityRequestPayload>({
            query: ({ corpCode, locationCode, reservationId }) => ({
                url: `/api/v1/reservation/${reservationId}/unit-availability`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<RapidStorUnitAvailability>) => {
                return resp.results;
            },
        }),
        postScaReservationWithFeePrepare: builder.mutation<ScaPrepareResponse, PostScaReservationWithFeePrepare>({
            query: ({ locationCode, corpCode, payload }) => ({
                url: `/api/v1/sca/prepare/reservation-fee`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<ScaPrepareResponse>
            ) => {
                return resp.results;
            },
        }),
        postScaMoveInPrepare: builder.mutation<ScaPrepareResponse, PostScaMoveInPrepare>({
            query: ({ locationCode, corpCode, payload }) => ({
                url: `/api/v1/sca/prepare/move-in`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<ScaPrepareResponse>
            ) => {
                return resp.results;
            },
        }),
        postScaMoveInBillingInfoUpdate: builder.mutation<ScaPrepareResponse, PostScaBillingInfoUpdate>({
            query: ({ locationCode, corpCode, payload }) => ({
                url: `/api/v1/sca/prepare/move-in/billing-info-update`,
                method: "POST",
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<ScaPrepareResponse>
            ) => {
                return resp.results;
            }
        }),
        getMoveInWizardInitialisation: builder.query({
            query: ({ corpToken, locationToken, reservationToken }) => ({
                url: `/rapidstor/${corpToken}/${locationToken}/api/wizard/movein/single-page/initilisation`,
                method: HttpMethod.POST,
                body: {
                    reservationToken: reservationToken,
                },
            }),
            transformResponse: (resp: RapidStorMoveInInit) => {
                return resp;
            },
        }),
        postMoveInWizardCostRetrieve: builder.mutation<
            CostRetrieveFormatted,
            RapidStorCostRetrieveRequest
        >({
            query: ({ corpToken, locationToken, payload }) => ({
                url: `/rapidstor/${corpToken}/${locationToken}/api/move-in/costretrieve`,
                method: HttpMethod.POST,
                body: payload,
            }),
        }),
        getMerchandise: builder.query<RapidStorMerchandise, BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: `/api/v1/merchandise`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorMerchandise>) => {
                return resp.results;
            },
        }),
        getBoxShopCoupon: builder.mutation({
            query: ({ corpToken, siteToken, code }) => ({
                url: `/rapidstor/${corpToken}/${siteToken}/api/boxshop/coupon/${code}`,
                method: HttpMethod.GET,
            }),
            transformResponse: (resp: RapidStorBoxShopCouponResponse) => {
                return resp;
            },
        }),
        postApplyCoupon: builder.mutation({
            query: ({ corpToken, siteToken, payload }) => ({
                url: `/rapidstor/${corpToken}/${siteToken}/api/deal/coupon/apply`,
                method: HttpMethod.POST,
                body: payload,
            }),
            transformResponse: (resp: RapidStorCouponRatesResponse) => {
                return resp;
            },
        }),
        getCouponRates: builder.mutation<RapidStorCouponRatesResponse, GetCouponRatesRequestPayload>({
            query: ({ couponCode, unitTypeId, corpCode, locationCode }) => ({
                url: `/api/v1/coupon/get-rates?code=${couponCode}&unitTypeId=${unitTypeId}`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<RapidStorCouponRatesResponse>
            ) => {
                return resp.results;
            },
        }),
        postMoveIn: builder.mutation({
            query: ({ locationCode, corpCode, payload, token }) => ({
                url: `/api/v1/move-in/${token}`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorMoveInResponse>) => {
                return resp;
            },
        }),
        postReservationSaveDocuments: builder.mutation<undefined, PostReservationSaveDocumentsPayload>({
            query: ({ documents, corpToken, locationToken, reservationToken, tenantId }) => ({
                url: `/rapidstor/${corpToken}/${locationToken}/api/reservation/${reservationToken}/${tenantId}/save-documents`,
                method: HttpMethod.POST,
                headers: {
                    'Content-Type': undefined,
                },
                body: documents,
            }),
        }),
        getMarketingSources: builder.query<Record<string, MarketingSource[]>, BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: `/api/v1/locations/${locationCode}/marketing-sources`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<MarketingSource[]>) => {
                return groupByLocationCode(resp.results);
            },
        }),
        postMarketingSurvey: builder.mutation<string, PostMarketingSurveyRequestPayload>({
            query: ({ locationCode, corpCode, tenantId, payload }) => ({
                url: `/api/v1/tenant/${tenantId}/marketing`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode
                },
            }),
            transformResponse: (resp: RapidStorResponse<string>) => {
                return resp.results;
            },
        }),
    }),
});

export const {
    useGetCorpQuery,
    useGetReservationQuery,

    usePostReservationMutation,
    usePostSaveQuoteMutation,
    usePostTrackEventMutation,

    usePatchReservationMutation,
    usePostReservationFeeMutation,

    useGetMoveInCostRetrieveMutation,
    useGetChargesQuery,
    useGetUnitAvailabilityMutation,

    usePostScaReservationWithFeePrepareMutation,
    usePostScaMoveInPrepareMutation,
    usePostScaMoveInBillingInfoUpdateMutation,

    useGetMoveInWizardInitialisationQuery,
    usePostMoveInWizardCostRetrieveMutation,
    usePostMoveInMutation,
    useGetMerchandiseQuery,

    usePostApplyCouponMutation,
    useGetCouponRatesMutation,
    useGetBoxShopCouponMutation,

    usePostReservationSaveDocumentsMutation,
    useGetMarketingSourcesQuery,
    usePostMarketingSurveyMutation,

    useGetLocationByCodeQuery,
    useGetUnitListByTypeQuery,
    useGetPersonasQuery,
} = rapidstorApi;
